import React, { useEffect, useRef, useState } from "react"
import { Input, Modal, Select } from "antd"
import type { NetOption } from "./AddNetModal.component"

export interface AddDifferenceModalProps {
  open: boolean
  /**
   * The rows a difference can be made of, in the table's order: each by what
   * the table stores (a net's name, an option's label) and the name it is
   * shown under.
   */
  rows: NetOption[]
  onCancel: () => void
  /** Adds the difference `plus` minus `minus`; returns why it was refused, or null once it is added. */
  onConfirm: (name: string, plus: string, minus: string) => string | null
}

/** The list opens inside the dialog, so it is inside the spec and gets its styles. */
const insideDialog = (trigger: HTMLElement): HTMLElement =>
  trigger.parentElement ?? document.body

/**
 * Asks for a difference row: its name and two rows of the table, the first
 * minus the second, as NPS is Promoters minus Detractors. Each opening
 * starts empty. A refusal from `onConfirm` is shown under the name and the
 * dialog stays open; the caller closes it once the row is added.
 */
const AddDifferenceModal: React.FC<AddDifferenceModalProps> = ({
  open,
  rows,
  onCancel,
  onConfirm,
}) => {
  const [name, setName] = useState("")
  const [plus, setPlus] = useState<string | null>(null)
  const [minus, setMinus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  // The dialog renders inside the spec, so it gets the --qc-* tokens.
  const container = (): HTMLElement =>
    anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body

  useEffect(() => {
    if (!open) return
    setName("")
    setPlus(null)
    setMinus(null)
    setError(null)
  }, [open])

  const confirm = (): void => setError(onConfirm(name, plus ?? "", minus ?? ""))
  const side = (
    label: string,
    value: string | null,
    set: (v: string) => void
  ): React.ReactNode => (
    <Select
      showSearch
      aria-label={label}
      className="ts-difference-form__row"
      placeholder="Choose a row"
      value={value}
      options={rows}
      optionFilterProp="label"
      notFoundContent="No row matches"
      getPopupContainer={insideDialog}
      onChange={(v: string) => {
        set(v)
        setError(null)
      }}
    />
  )

  return (
    <>
      <span ref={anchorRef} hidden />
      <Modal
        open={open}
        title="Add difference"
        okText="Add"
        onOk={confirm}
        onCancel={onCancel}
        getContainer={container}
        destroyOnHidden
      >
        <div className="ts-net-form">
          <Input
            aria-label="Difference name"
            placeholder="Name"
            value={name}
            status={error ? "error" : undefined}
            onChange={(e) => {
              setName(e.target.value)
              setError(null)
            }}
            onPressEnter={confirm}
          />
          {error && (
            <p className="ts-error" role="alert">
              {error}
            </p>
          )}
          <div className="ts-difference-form">
            {side("First row", plus, setPlus)}
            <span className="ts-difference-form__minus">minus</span>
            {side("Second row", minus, setMinus)}
          </div>
          <p className="ts-hint">
            The first row&apos;s column percentage minus the second&apos;s, in points.
          </p>
        </div>
      </Modal>
    </>
  )
}

export default AddDifferenceModal
